import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import AttachmentsViewer from '@ui-lib/components/AttachmentsViewer';
import { createSx } from '@ui-lib/utils';
import { DateISOString } from '@web-shared/utils/time/types';

import { EbolPhoto } from '../../api/types/EbolDetails';
import { PHOTO_TYPE, PHOTO_TYPE_LABELS } from '../../api/types/PhotoType';
import { BolPhotoAttachment } from './types';
import PhotoItem from './components/PhotoItem';
import { PhotoDetails } from './components/PhotoDetails';

interface Props {
  photo?: string;
  photos?: EbolPhoto[];
  createdAt?: DateISOString;
}

const sx = createSx({
  root: {
    width: '100%',
    overflowX: 'auto',
  },
});

export const PhotoPreview = ({ photos, photo, createdAt }: Props) => {
  const { t } = useTranslation();
  const attachments = useMemo<BolPhotoAttachment[]>(
    () => {
      const date = createdAt ?? new Date().toISOString();
      if (photo) {
        return [{
          url: photo,
          name: t(PHOTO_TYPE_LABELS[PHOTO_TYPE.ORIGINAL]),
          createdAt: date,
        }];
      }

      return photos?.map<BolPhotoAttachment>((photo) => ({
        url: photo.picture_url,
        name: photo.name ?? t(PHOTO_TYPE_LABELS[photo.photo_type!]),
        createdAt: date,
      })) ?? new Array<BolPhotoAttachment>();
    }, [photos, photo, t, createdAt]);

  return (
    <Box sx={sx.root}>
      <AttachmentsViewer
        attachments={attachments}
        AttachmentComponent={PhotoItem}
        AttachmentDetails={PhotoDetails}
        maxAttachmentCount={attachments.length}
      />
    </Box>
  );
};
